import React, { useEffect, useRef, useState } from "react";

interface ProfileImagePreviewProps {
  coverImageUrl?: string;
  profileImageUrl?: string;
  name?: string;
  onCoverUpload: (croppedUrl: string, originalUrl: string) => void;
  onProfileUpload: (croppedUrl: string, originalUrl: string) => void;
  showProfileImage?: boolean;
}

declare global {
  interface Window {
    cloudinary: any;
  }
}

const ProfileImagePreview: React.FC<ProfileImagePreviewProps> = ({
  coverImageUrl,
  profileImageUrl,
  name,
  onCoverUpload,
  onProfileUpload,
  showProfileImage = true,
}) => {
  const coverWidgetRef = useRef<any>(null);
  const profileWidgetRef = useRef<any>(null);
  const [apiKey, setApiKey] = useState<string>("");
  const [cloudName, setCloudName] = useState<string>("");

  // Fetch API key and cloud name on mount
  useEffect(() => {
    fetch("/api/cloudinary-config")
      .then((res) => res.json())
      .then((data) => {
        setApiKey(data.apiKey);
        setCloudName(data.cloudName);
      })
      .catch((error) => console.error("Error fetching Cloudinary config:", error));
  }, []);

  // Initialize cover image widget
  useEffect(() => {
    if (typeof window !== "undefined" && window.cloudinary && apiKey && cloudName) {
      coverWidgetRef.current = window.cloudinary.createUploadWidget(
        {
          cloudName: cloudName,
          apiKey: apiKey,
          uploadSignature: async (callback: any, paramsToSign: any) => {
            try {
              const response = await fetch("/api/cloudinary-signature", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ paramsToSign }),
              });
              const data = await response.json();
              callback(data.signature);
            } catch (error) {
              console.error("Error fetching signature:", error);
            }
          },
          folder: "listings/covers",
          cropping: true,
          croppingAspectRatio: 1.5,
          croppingShowDimensions: true,
          croppingCoordinatesMode: "custom",
          showSkipCropButton: false,
          sources: ["local", "url", "camera"],
          maxFiles: 1,
          clientAllowedFormats: ["jpg", "jpeg", "png", "gif", "webp"],
          maxFileSize: 10000000,
          resourceType: "image",
          eager: "c_crop,g_custom",
        },
        (error: any, result: any) => {
          if (error) {
            console.error("Upload Widget error:", error);
            return;
          }

          if (result.event === "success") {
            console.log("Cover upload successful:", result.info);

            const originalUrl = result.info.secure_url;
            let croppedUrl = originalUrl;

            if (result.info.eager && result.info.eager.length > 0) {
              croppedUrl = result.info.eager[0].secure_url;
            } else if (
              result.info.coordinates &&
              result.info.coordinates.custom &&
              result.info.coordinates.custom.length > 0
            ) {
              const coords = result.info.coordinates.custom[0];
              const x = coords[0] !== undefined ? coords[0] : coords.x;
              const y = coords[1] !== undefined ? coords[1] : coords.y;
              const width = coords[2] !== undefined ? coords[2] : coords.width;
              const height = coords[3] !== undefined ? coords[3] : coords.height;

              if (
                x !== undefined &&
                y !== undefined &&
                width !== undefined &&
                height !== undefined
              ) {
                const publicId = result.info.public_id;
                croppedUrl = `https://res.cloudinary.com/${cloudName}/image/upload/c_crop,x_${x},y_${y},w_${width},h_${height}/${publicId}.${result.info.format}`;
              }
            }

            onCoverUpload(croppedUrl, originalUrl);
          }
        }
      );
    }

    return () => {
      if (coverWidgetRef.current) {
        coverWidgetRef.current.destroy();
      }
    };
  }, [apiKey, cloudName, onCoverUpload]);

  // Initialize profile image widget (only if showProfileImage is true)
  useEffect(() => {
    if (
      showProfileImage &&
      typeof window !== "undefined" &&
      window.cloudinary &&
      apiKey &&
      cloudName
    ) {
      profileWidgetRef.current = window.cloudinary.createUploadWidget(
        {
          cloudName: cloudName,
          apiKey: apiKey,
          uploadSignature: async (callback: any, paramsToSign: any) => {
            try {
              const response = await fetch("/api/cloudinary-signature", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ paramsToSign }),
              });
              const data = await response.json();
              callback(data.signature);
            } catch (error) {
              console.error("Error fetching signature:", error);
            }
          },
          folder: "listings/profiles",
          cropping: true,
          croppingAspectRatio: 1,
          croppingShowDimensions: true,
          croppingCoordinatesMode: "custom",
          showSkipCropButton: false,
          sources: ["local", "url", "camera"],
          maxFiles: 1,
          clientAllowedFormats: ["jpg", "jpeg", "png", "gif", "webp"],
          maxFileSize: 10000000,
          resourceType: "image",
          eager: "c_crop,g_custom",
        },
        (error: any, result: any) => {
          if (error) {
            console.error("Upload Widget error:", error);
            return;
          }

          if (result.event === "success") {
            console.log("Profile upload successful:", result.info);

            const originalUrl = result.info.secure_url;
            let croppedUrl = originalUrl;

            if (result.info.eager && result.info.eager.length > 0) {
              croppedUrl = result.info.eager[0].secure_url;
            } else if (
              result.info.coordinates &&
              result.info.coordinates.custom &&
              result.info.coordinates.custom.length > 0
            ) {
              const coords = result.info.coordinates.custom[0];
              const x = coords[0] !== undefined ? coords[0] : coords.x;
              const y = coords[1] !== undefined ? coords[1] : coords.y;
              const width = coords[2] !== undefined ? coords[2] : coords.width;
              const height = coords[3] !== undefined ? coords[3] : coords.height;

              if (
                x !== undefined &&
                y !== undefined &&
                width !== undefined &&
                height !== undefined
              ) {
                const publicId = result.info.public_id;
                croppedUrl = `https://res.cloudinary.com/${cloudName}/image/upload/c_crop,x_${x},y_${y},w_${width},h_${height}/${publicId}.${result.info.format}`;
              }
            }

            onProfileUpload(croppedUrl, originalUrl);
          }
        }
      );
    }

    return () => {
      if (profileWidgetRef.current) {
        profileWidgetRef.current.destroy();
      }
    };
  }, [apiKey, cloudName, onProfileUpload, showProfileImage]);

  const handleCoverClick = () => {
    if (coverWidgetRef.current) {
      coverWidgetRef.current.open();
    }
  };

  const handleProfileClick = () => {
    if (profileWidgetRef.current) {
      profileWidgetRef.current.open();
    }
  };

  return (
    <div className="profile-image-preview">
      {/* Cover Image Background */}
      <div
        className={`cover-image-container ${!coverImageUrl ? "cover-upload-zone" : ""}`}
        style={{
          backgroundImage: coverImageUrl ? `url(${coverImageUrl})` : "none",
          backgroundColor: coverImageUrl ? "transparent" : "#f9f9f9",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          cursor: "pointer",
        }}
        onClick={handleCoverClick}
      >
        {!coverImageUrl && (
          <div className="cover-placeholder">
            <svg
              width="32"
              height="32"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span>Add Cover Image</span>
          </div>
        )}
      </div>

      {/* Profile Image Overlay - only show if showProfileImage is true */}
      {showProfileImage && (
        <div className="profile-image-container">
          <div
            className={`profile-image-circle ${!profileImageUrl ? "profile-upload-zone" : ""}`}
            style={{ cursor: "pointer" }}
            onClick={handleProfileClick}
          >
            {profileImageUrl ? (
              <img
                src={profileImageUrl}
                alt="Profile"
                className="profile-image"
              />
            ) : (
              <div className="profile-placeholder">
                <svg
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileImagePreview;
